import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, Route, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { api, apiBaseUrl } from "@/lib/api.js";
import { useAuthStore } from "@/lib/auth.js";
import type {
  DlrCampaignSummary,
  DlrRecipient,
  DlrTimelineEvent,
} from "@bo/shared";

const STATUS_STYLE: Record<string, string> = {
  enqueued: "badge-neutral",
  sent: "badge-info",
  delivered: "badge-success",
  read: "badge-success",
  failed: "badge-danger",
  "lista negra": "badge-warn",
};
const STATUS_ES: Record<string, string> = {
  enqueued: "En cola",
  sent: "Enviado",
  delivered: "Entregado",
  read: "Leído",
  failed: "Falló",
  "lista negra": "Lista negra",
};

function fmtGT(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-GT", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "America/Guatemala",
  });
}

function StatCard({
  label,
  value,
  pct,
  tone,
}: {
  label: string;
  value: number | string;
  pct?: string;
  tone?: "ok" | "warn" | "bad" | "info";
}) {
  const color =
    tone === "ok"
      ? "text-emerald-600"
      : tone === "bad"
        ? "text-rose-600"
        : tone === "warn"
          ? "text-amber-600"
          : tone === "info"
            ? "text-blue-600"
            : "";
  return (
    <div className="card p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {pct && <div className="text-xs text-slate-400 mt-0.5">{pct}</div>}
    </div>
  );
}

// ---- Timeline modal de un mensaje -----------------------------------------
function TimelineModal({
  messageId,
  window,
  onClose,
}: {
  messageId: string;
  window: { from: string; to: string };
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dlr-timeline", messageId, window.from, window.to],
    queryFn: () =>
      api.get<{ events: DlrTimelineEvent[] }>(
        `/dlr/message/${encodeURIComponent(messageId)}/timeline?from=${window.from}&to=${window.to}`,
      ),
  });
  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 grid place-items-center p-4">
      <div className="card w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-lg">Timeline del mensaje</h2>
            <p className="text-xs mono text-slate-500 mt-1 break-all">{messageId}</p>
          </div>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        {isLoading ? (
          <p className="text-sm text-slate-400">Cargando…</p>
        ) : !data?.events.length ? (
          <p className="text-sm text-slate-400">Sin eventos DLR para este mensaje.</p>
        ) : (
          <ol className="space-y-3">
            {data.events.map((e, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className={STATUS_STYLE[e.status] ?? "badge-neutral"}>
                  {STATUS_ES[e.status] ?? e.status}
                </span>
                <span className="text-sm text-slate-500">{fmtGT(e.at)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export function DlrTracesTab({
  campaignId,
  onPickCampaign,
}: {
  campaignId: number | null;
  onPickCampaign: (id: number | null) => void;
}) {
  const [input, setInput] = useState(campaignId ? String(campaignId) : "");
  const [page, setPage] = useState(1);
  const [timeline, setTimeline] = useState<{ messageId: string } | null>(null);
  const canExport = useAuthStore((s) => s.hasPermission("dlr.export"));
  const pageSize = 50;

  const summary = useQuery({
    queryKey: ["dlr-summary", campaignId],
    queryFn: () =>
      api.get<DlrCampaignSummary>(`/dlr/campaign/${campaignId}/summary`),
    enabled: !!campaignId,
  });

  const recipients = useQuery({
    queryKey: ["dlr-recipients", campaignId, page],
    queryFn: () =>
      api.get<{ items: DlrRecipient[]; total: number; window: { from: string; to: string } }>(
        `/dlr/campaign/${campaignId}/recipients?page=${page}&pageSize=${pageSize}`,
      ),
    enabled: !!campaignId,
  });

  const win = summary.data?.window;

  const cols: Column<DlrRecipient>[] = useMemo(
    () => [
      { header: "Teléfono", render: (r) => <span className="mono text-xs">{r.telefono}</span> },
      {
        header: "Estado",
        render: (r) =>
          r.status ? (
            <span className={STATUS_STYLE[r.status] ?? "badge-neutral"}>
              {STATUS_ES[r.status] ?? r.status}
            </span>
          ) : (
            <span className="badge-neutral">Sin reporte</span>
          ),
      },
      { header: "Última actualización", render: (r) => fmtGT(r.statusAt) },
      {
        header: "",
        width: "110px",
        render: (r) =>
          r.messageId ? (
            <button
              className="btn-ghost text-xs"
              onClick={() => setTimeline({ messageId: r.messageId! })}
            >
              <Route size={12} className="mr-1" /> Timeline
            </button>
          ) : null,
      },
    ],
    [],
  );

  async function downloadCsv() {
    if (!campaignId) return;
    try {
      let token = useAuthStore.getState().accessToken;
      if (!token) {
        await useAuthStore.getState().tryRefresh();
        token = useAuthStore.getState().accessToken;
      }
      if (!token) throw new Error("Sesión expirada, vuelve a entrar");
      const res = await fetch(`${apiBaseUrl}/dlr/campaign/${campaignId}/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`No se pudo exportar (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dlr-campana-${campaignId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const s = summary.data;
  const total = s?.totalRecipients ?? 0;
  const bs = s?.byStatus ?? {};
  const pct = (n: number) => (total ? `${Math.round((n / total) * 100)}%` : "");

  return (
    <div className="space-y-5">
      <div className="flex gap-3 flex-wrap items-end">
        <div className="relative flex-1 max-w-xs">
          <label className="text-xs text-slate-500">ID de campaña (plantilla)</label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Ej: 42806"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && input) {
                  setPage(1);
                  onPickCampaign(Number(input));
                }
              }}
            />
          </div>
        </div>
        <button
          className="btn-primary"
          disabled={!input}
          onClick={() => {
            setPage(1);
            onPickCampaign(Number(input));
          }}
        >
          Ver trazas
        </button>
        {campaignId && canExport && (
          <button className="btn-secondary ml-auto" onClick={downloadCsv}>
            <Download size={14} className="mr-1" /> Exportar CSV
          </button>
        )}
      </div>

      {!campaignId && (
        <div className="card p-8 text-center text-slate-400 text-sm">
          Ingresa un ID de campaña (o entra desde “Ver trazas” en Campañas programadas)
          para ver los DLRs: enviados, entregados, leídos y fallidos.
        </div>
      )}

      {campaignId && summary.isError && (
        <div className="alert-danger flex items-center gap-2 text-sm">
          <AlertCircle size={16} /> No se pudieron cargar las trazas (¿faltan permisos de BD?).
        </div>
      )}

      {campaignId && s && (
        <>
          <div>
            <div className="text-sm">
              <span className="font-semibold">{s.nombre ?? s.templateName ?? `Campaña ${s.idplantilla}`}</span>
              {s.companyName && <span className="text-slate-500"> · {s.companyName}</span>}
              {s.cuenta && <span className="text-slate-500 mono text-xs"> · {s.cuenta}</span>}
            </div>
            <div className="text-xs text-slate-400">
              Programada: {fmtGT(s.scheduledAt)} · Ventana consultada: {s.window.from} → {s.window.to}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard label="Destinatarios" value={total} />
            <StatCard label="Enviados" value={bs.sent ?? 0} pct={pct(bs.sent ?? 0)} tone="info" />
            <StatCard label="Entregados" value={bs.delivered ?? 0} pct={pct(bs.delivered ?? 0)} tone="ok" />
            <StatCard label="Leídos" value={bs.read ?? 0} pct={pct(bs.read ?? 0)} tone="ok" />
            <StatCard label="Fallidos" value={bs.failed ?? 0} pct={pct(bs.failed ?? 0)} tone="bad" />
            <StatCard label="Sin reporte" value={s.sinReporte} tone="warn" />
          </div>

          <DataTable
            columns={cols}
            rows={recipients.data?.items}
            loading={recipients.isLoading}
            getKey={(r) => `${r.telefono}-${r.messageId}`}
            empty="Sin destinatarios en la ventana consultada."
          />

          {recipients.data && recipients.data.total > pageSize && (
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, recipients.data.total)} de{" "}
                {recipients.data.total}
              </span>
              <div className="flex gap-2">
                <button className="btn-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <button
                  className="btn-secondary"
                  disabled={page * pageSize >= recipients.data.total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400">
            Los DLRs se leen de lo que Gina ya registra (SM_NOTIFICACION_WAPP), acotado a la
            partición de fecha de la campaña. Horas en zona Guatemala (UTC-6).
          </p>
        </>
      )}

      {timeline && win && (
        <TimelineModal
          messageId={timeline.messageId}
          window={win}
          onClose={() => setTimeline(null)}
        />
      )}
    </div>
  );
}
